/**
 * PSV-1728 — client-side crystal cargo pickups.
 *
 * Server is authoritative for crystal LIFECYCLE (spawn / collect / expire)
 * but pushes zero per-crystal motion traffic: one `CrystalBurst` carries
 * each crystal's initial position + velocity and the client mirrors the
 * ballistic scatter locally with the same friction integral the server
 * runs (`v *= CRYSTAL_FRICTION_PER_S ** dt; pos += v * dt`). Endings:
 *   - `CrystalCollected` → short flight to the collector ship + scale-pop
 *   - `CrystalsExpired`  → 300ms fade-out
 *   - local TTL fallback → if `expiresAt` passes with no event (event
 *     loss tolerance) the crystal fades itself out anyway
 *
 * Visual: one Graphics per crystal — a gold diamond (a square rotated
 * 45°, drawn with fillRect/strokeRect; the test Phaser mock has no
 * fillPoints/path surface) with a slow continuous spin and a subtle sine
 * scale pulse for sparkle. Bigger value → slightly bigger diamond.
 * Spin/pulse write the Transform properties (`rotation`, `scaleX/Y`)
 * directly instead of calling setters — keeps update() allocation-free
 * and stays inside the mocked API surface for ArenaController tests.
 */

import Phaser from 'phaser';

import {
  CRYSTAL_FRICTION_PER_S,
  CRYSTAL_MAGNET_ACCEL,
  CRYSTAL_MAGNET_RADIUS_PX,
} from '../../../server/shared/crystals';

const CRYSTAL_FILL = 0xffd84a;
const CRYSTAL_STROKE = 0xffefa8; // lighter rim so the gem reads on dark bg

// Square whose diagonal (point-to-point height once rotated 45°) is 10px.
const CRYSTAL_HALF_SIDE_PX = 10 / (2 * Math.SQRT2);

// Asteroids sit at -10 (HP bars -9), ships at 0 — crystals go between:
// above the rocks they scattered from, below the ship that scoops them.
const CRYSTAL_DEPTH = -5;

const SPIN_RAD_PER_S = 1.5;
const PULSE_RAD_PER_S = 6;
const PULSE_AMPLITUDE = 0.1;

const COLLECT_FLIGHT_MS = 200; // fly to the collector ship
const COLLECT_POP_MS = 120; // then a tiny flash/scale-pop
const EXPIRE_FADE_MS = 300;
// Local TTL fires this long AFTER expiresAt — gives the authoritative
// `CrystalsExpired` broadcast first claim before the fallback kicks in.
const TTL_GRACE_MS = 250;

interface CrystalRenderEntry {
  id: string;
  gfx: Phaser.GameObjects.Graphics;
  // Locally-simulated ballistics (authoritative only at burst time).
  x: number;
  y: number;
  vx: number;
  vy: number;
  expiresAt: number; // ms epoch, from the burst payload
  baseScale: number;
  spin: number; // rad; starts at π/4 (square → diamond)
  pulsePhase: number; // random per crystal so a burst doesn't pulse in sync
  ageS: number;
  // 'live' → simulated in update(); 'fading'/'collected' → a tween owns
  // the object until finalize() despawns it.
  state: 'live' | 'fading' | 'collected';
  activeTween: Phaser.Tweens.Tween | null;
}

export class CrystalManager {
  private readonly scene: Phaser.Scene;
  private readonly getShipPosition: (
    userId: string,
  ) => { x: number; y: number } | null;
  private readonly getMagnetTargets: () => Array<{ x: number; y: number }>;
  private readonly entries = new Map<string, CrystalRenderEntry>();

  constructor(
    scene: Phaser.Scene,
    getShipPosition: (userId: string) => { x: number; y: number } | null,
    getMagnetTargets: () => Array<{ x: number; y: number }> = () => [],
  ) {
    this.scene = scene;
    this.getShipPosition = getShipPosition;
    this.getMagnetTargets = getMagnetTargets;
  }

  /** Server shattered something — materialize the scatter. Idempotent per id. */
  spawnBurst(
    crystals: Array<{
      id: string;
      x: number;
      y: number;
      vx: number;
      vy: number;
      value: number;
      expiresAt: number;
    }>,
  ): void {
    for (const c of crystals) {
      if (this.entries.has(c.id)) continue; // idempotent
      const gfx = this.scene.add.graphics();
      gfx.fillStyle(CRYSTAL_FILL, 1);
      gfx.fillRect(
        -CRYSTAL_HALF_SIDE_PX,
        -CRYSTAL_HALF_SIDE_PX,
        CRYSTAL_HALF_SIDE_PX * 2,
        CRYSTAL_HALF_SIDE_PX * 2,
      );
      gfx.lineStyle(1, CRYSTAL_STROKE, 0.9);
      gfx.strokeRect(
        -CRYSTAL_HALF_SIDE_PX,
        -CRYSTAL_HALF_SIDE_PX,
        CRYSTAL_HALF_SIDE_PX * 2,
        CRYSTAL_HALF_SIDE_PX * 2,
      );
      gfx.setPosition(c.x, c.y);
      gfx.setDepth(CRYSTAL_DEPTH);

      // Fatter crystal → slightly bigger gem, capped so a jackpot doesn't
      // dwarf the ships.
      const baseScale = 0.8 + Math.min(0.6, c.value * 0.06);
      const spin = Math.PI / 4;
      gfx.rotation = spin;
      gfx.scaleX = baseScale;
      gfx.scaleY = baseScale;

      this.entries.set(c.id, {
        id: c.id,
        gfx,
        x: c.x,
        y: c.y,
        vx: c.vx,
        vy: c.vy,
        expiresAt: c.expiresAt,
        baseScale,
        spin,
        pulsePhase: Math.random() * Math.PI * 2,
        ageS: 0,
        state: 'live',
        activeTween: null,
      });
    }
  }

  /**
   * Server credited a pickup — fly the crystal to the collector's current
   * position (captured once at call time; no per-frame re-targeting) and
   * pop. The credit is authoritative, so a collect overrides an expire /
   * TTL fade already in flight. No ship position → just despawn.
   */
  collect(id: string, userId: string): void {
    const entry = this.entries.get(id);
    if (entry === undefined || entry.state === 'collected') return;
    if (entry.activeTween !== null) {
      entry.activeTween.remove();
      entry.activeTween = null;
    }
    entry.state = 'collected';

    const target = this.getShipPosition(userId);
    if (target === null) {
      this.finalize(entry);
      return;
    }

    entry.gfx.alpha = 1; // may have been mid-fade
    entry.activeTween = this.scene.tweens.add({
      targets: entry.gfx,
      x: target.x,
      y: target.y,
      scaleX: entry.baseScale * 0.6,
      scaleY: entry.baseScale * 0.6,
      duration: COLLECT_FLIGHT_MS,
      ease: 'Quad.easeIn',
      onComplete: () => this.popAndFinalize(entry),
    });
  }

  /** Server expired strays — fade them out. Unknown / ending ids are no-ops. */
  expire(ids: string[]): void {
    for (const id of ids) {
      const entry = this.entries.get(id);
      if (entry === undefined || entry.state !== 'live') continue;
      this.startFade(entry);
    }
  }

  /**
   * Called per Phaser frame from ArenaController.update(). Integrates the
   * shared ballistic model for live crystals, spins/pulses them, and
   * fires the local TTL fade when `expiresAt` passes unanswered.
   */
  update(deltaMs: number): void {
    const dt = deltaMs / 1000;
    if (dt <= 0) return;
    const friction = CRYSTAL_FRICTION_PER_S ** dt; // same factor for all
    const now = Date.now();
    // PSV-1902 — mirror the SERVER's magnet locally. Without it any
    // near-miss flyby dragged the server's crystal away from the local
    // ballistic rest position and every later pickup attempt aimed at a
    // ghost («кристаллы через раз затягиваются»). Same radius/accel
    // constants; eligibility (alive + hold space) is pre-filtered by the
    // controller's target provider.
    const targets = this.getMagnetTargets();
    const magnetSq = CRYSTAL_MAGNET_RADIUS_PX * CRYSTAL_MAGNET_RADIUS_PX;
    for (const entry of this.entries.values()) {
      if (entry.state !== 'live') continue; // tween owns fading/collected
      let tgt: { x: number; y: number } | null = null;
      let tgtDistSq = magnetSq;
      for (const t of targets) {
        const dx = t.x - entry.x;
        const dy = t.y - entry.y;
        const distSq = dx * dx + dy * dy;
        if (distSq <= tgtDistSq) {
          tgt = t;
          tgtDistSq = distSq;
        }
      }
      if (tgt !== null) {
        const dist = Math.max(1e-3, Math.sqrt(tgtDistSq));
        entry.vx += ((tgt.x - entry.x) / dist) * CRYSTAL_MAGNET_ACCEL * dt;
        entry.vy += ((tgt.y - entry.y) / dist) * CRYSTAL_MAGNET_ACCEL * dt;
      }
      entry.vx *= friction;
      entry.vy *= friction;
      entry.x += entry.vx * dt;
      entry.y += entry.vy * dt;
      entry.gfx.setPosition(entry.x, entry.y);

      entry.spin += dt * SPIN_RAD_PER_S;
      entry.gfx.rotation = entry.spin;
      entry.ageS += dt;
      const scale =
        entry.baseScale *
        (1 +
          PULSE_AMPLITUDE *
            Math.sin(entry.ageS * PULSE_RAD_PER_S + entry.pulsePhase));
      entry.gfx.scaleX = scale;
      entry.gfx.scaleY = scale;

      if (now >= entry.expiresAt + TTL_GRACE_MS) {
        this.startFade(entry); // event-loss fallback
      }
    }
  }

  /** Idempotent teardown — kill every sprite + tween. */
  destroy(): void {
    for (const entry of this.entries.values()) {
      if (entry.activeTween !== null) entry.activeTween.remove();
      entry.gfx.destroy();
    }
    this.entries.clear();
  }

  // ---------- private ----------

  private startFade(entry: CrystalRenderEntry): void {
    entry.state = 'fading';
    entry.activeTween = this.scene.tweens.add({
      targets: entry.gfx,
      alpha: 0,
      duration: EXPIRE_FADE_MS,
      ease: 'Quad.easeOut',
      onComplete: () => this.finalize(entry),
    });
  }

  /** Second leg of the collect animation: tiny flash/scale-pop, then gone. */
  private popAndFinalize(entry: CrystalRenderEntry): void {
    entry.activeTween = this.scene.tweens.add({
      targets: entry.gfx,
      scaleX: entry.baseScale * 1.6,
      scaleY: entry.baseScale * 1.6,
      alpha: 0,
      duration: COLLECT_POP_MS,
      ease: 'Quad.easeOut',
      onComplete: () => this.finalize(entry),
    });
  }

  private finalize(entry: CrystalRenderEntry): void {
    entry.activeTween = null;
    entry.gfx.destroy();
    this.entries.delete(entry.id);
  }
}
